<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-ink">{{ t("reflections.admin.settings.title") }}</h2>
            <p class="body-small text-ink-muted">{{ t("reflections.admin.settings.subtitle") }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }} </BaseButton>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else>
            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.settings.raterPrivacy") }}</div>
                    <label for="anonymize-rater-status" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.settings.anonymizeTitle") }}</span>
                            <span class="body-small text-ink-muted">
                                {{ t("reflections.admin.settings.anonymizeHelp") }}
                            </span>
                        </span>
                        <BaseSwitch id="anonymize-rater-status" v-model="anonymizeRaterStatus" size="md" class="mt-1" />
                    </label>
                </div>
            </BaseCard>

            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.settings.raterNomination") }}</div>
                    <label for="subject-specifies-relationship" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.settings.categorizeTitle") }}</span>
                            <span class="body-small text-ink-muted">
                                {{ t("reflections.admin.settings.categorizeHelp") }}
                            </span>
                        </span>
                        <BaseSwitch id="subject-specifies-relationship" v-model="subjectSpecifiesRelationship" size="md" class="mt-1" />
                    </label>

                    <label for="allow-any-rater-email-domain" class="flex items-start justify-between gap-6 cursor-pointer">
                        <span class="flex flex-col gap-1">
                            <span class="body-small-bold text-ink">{{ t("reflections.admin.settings.outsideTitle") }}</span>
                            <span class="body-small text-ink-muted">
                                {{ t("reflections.admin.settings.outsideHelp") }}
                            </span>
                        </span>
                        <BaseSwitch id="allow-any-rater-email-domain" v-model="allowAnyRaterEmailDomain" size="md" class="mt-1" />
                    </label>
                </div>
            </BaseCard>

            <BaseCard padding="lg">
                <div class="flex flex-col gap-6">
                    <div class="flex flex-col gap-1">
                        <div class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.settings.raterCategories") }}</div>
                        <p class="body-small text-ink-muted">
                            {{ t("reflections.admin.settings.categoriesHelp") }}
                        </p>
                    </div>
                    <div class="flex flex-col gap-5 sm:gap-3">
                        <div class="hidden sm:flex items-center gap-4 scaffold-details-small text-ink-muted">
                            <span class="w-9 shrink-0">{{ t("reflections.admin.settings.columnOn") }}</span>
                            <span class="w-40 shrink-0">{{ t("reflections.admin.settings.columnCategory") }}</span>
                            <span>{{ t("reflections.admin.settings.columnLabel") }}</span>
                        </div>
                        <!-- Stacked on mobile; `sm:contents` dissolves the switch/name pair
                             into the row so the three columns line up on wider screens. -->
                        <div v-for="row in relationshipRows" :key="row.value" class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                            <div class="flex items-center gap-3 sm:contents">
                                <div class="sm:w-9 sm:shrink-0">
                                    <BaseSwitch
                                        :id="`relationship-${row.value}`"
                                        v-model="row.active"
                                        size="md"
                                        :aria-label="t('reflections.admin.settings.enableCategory', { label: row.defaultLabel })" />
                                </div>
                                <span class="body-small sm:w-40 sm:shrink-0" :class="row.active ? 'text-ink' : 'text-ink-muted'">
                                    {{ row.defaultLabel }}
                                </span>
                            </div>
                            <input
                                v-model="row.label"
                                type="text"
                                maxlength="64"
                                :placeholder="row.defaultLabel"
                                :disabled="!row.active"
                                :aria-label="t('reflections.admin.settings.labelFor', { label: row.defaultLabel })"
                                class="body-small text-ink border border-control-border rounded-lgr px-3 py-2 w-full sm:w-64 disabled:text-ink-muted disabled:bg-control-disabled-bg" />
                        </div>
                    </div>
                    <BaseNotification v-if="relationshipsError" theme="error" :text="relationshipsError" hide-dismiss />
                </div>
            </BaseCard>

            <BaseNotification v-if="saveStatus" :theme="saveStatus.theme" :text="saveStatus.text" class="mb-2" @dismiss="saveStatus = null" />

            <div class="flex items-center justify-end gap-3">
                <BaseButton theme="neutral" size="sm" :disabled="saving || !isDirty" @click="reset">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="saving || !isDirty || !!relationshipsError" @click="showSaveConfirm = true">
                    {{ saving ? t("reflections.admin.settings.saving") : t("reflections.admin.settings.save") }}
                </BaseButton>
            </div>

            <SubmitConfirmDialog
                v-model:open="showSaveConfirm"
                :title="t('reflections.admin.settings.confirmTitle')"
                :confirm-label="t('reflections.admin.settings.confirmAction')"
                :cancel-label="t('common.cancel')"
                :confirm-disabled="saving"
                @confirm="save">
                <p class="body text-ink-muted">
                    {{ t("reflections.admin.settings.confirmBody") }}
                </p>
            </SubmitConfirmDialog>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AssessmentAdminSettingsOutput, RaterRelationshipOption } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SubmitConfirmDialog from "~vue/pages/reflections/SubmitConfirmDialog.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits<{
    (event: "back"): void;
}>();
const { t } = useI18n();

const loading = ref(true);
const loadError = ref<string | null>(null);
const saving = ref(false);
const saveStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);
const showSaveConfirm = ref(false);

interface RelationshipRow {
    value: RaterRelationshipOption["value"];
    defaultLabel: string;
    label: string;
    active: boolean;
}

const anonymizeRaterStatus = ref(false);
const subjectSpecifiesRelationship = ref(false);
const allowAnyRaterEmailDomain = ref(false);
const relationshipRows = ref<RelationshipRow[]>([]);
const saved = ref<AssessmentAdminSettingsOutput>({ anonymize_rater_status: false, subject_specifies_relationship: false, allow_any_rater_email_domain: false });

const selectedRelationships = computed<RaterRelationshipOption[]>(() =>
    relationshipRows.value.filter((row) => row.active).map((row) => ({ value: row.value, label: row.label.trim() || row.defaultLabel })),
);

const relationshipsError = computed(() => (relationshipRows.value.some((row) => row.active) ? null : t("reflections.admin.settings.noCategories")));

const isDirty = computed(
    () =>
        anonymizeRaterStatus.value !== (saved.value.anonymize_rater_status ?? false) ||
        subjectSpecifiesRelationship.value !== (saved.value.subject_specifies_relationship ?? false) ||
        allowAnyRaterEmailDomain.value !== (saved.value.allow_any_rater_email_domain ?? false) ||
        JSON.stringify(selectedRelationships.value) !== JSON.stringify(saved.value.relationships ?? []),
);

function apply(settings: AssessmentAdminSettingsOutput) {
    saved.value = settings;
    anonymizeRaterStatus.value = settings.anonymize_rater_status ?? false;
    subjectSpecifiesRelationship.value = settings.subject_specifies_relationship ?? false;
    allowAnyRaterEmailDomain.value = settings.allow_any_rater_email_domain ?? false;
    const activeByValue = new Map((settings.relationships ?? []).map((option) => [option.value, option.label]));
    relationshipRows.value = (settings.available_relationships ?? []).map((option) => ({
        value: option.value,
        defaultLabel: option.label,
        label: activeByValue.get(option.value) ?? option.label,
        active: activeByValue.has(option.value),
    }));
}

function reset() {
    apply(saved.value);
    saveStatus.value = null;
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/settings").json<AssessmentAdminSettingsOutput>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("reflections.admin.settings.loadFailed");
        return;
    }
    if (data.value) {
        apply(data.value);
    }
});

async function save() {
    if (saving.value || !isDirty.value || relationshipsError.value) {
        showSaveConfirm.value = false;
        return;
    }
    saving.value = true;
    saveStatus.value = null;
    const body: AssessmentAdminSettingsOutput = {
        anonymize_rater_status: anonymizeRaterStatus.value,
        subject_specifies_relationship: subjectSpecifiesRelationship.value,
        allow_any_rater_email_domain: allowAnyRaterEmailDomain.value,
        relationships: selectedRelationships.value,
    };
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/settings").put(JSON.stringify(body), "application/json").json<AssessmentAdminSettingsOutput>();
    saving.value = false;
    showSaveConfirm.value = false;
    if (fetchError.value || !data.value) {
        saveStatus.value = { theme: "error", text: t("reflections.admin.settings.saveFailed") };
        return;
    }
    apply(data.value);
    saveStatus.value = { theme: "success", text: t("reflections.admin.settings.saved") };
}
</script>
